import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import { formatCurrency } from '../utils/currency';
import toast from 'react-hot-toast';
import {
  ArrowLeftIcon,
  BanknotesIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

const today = new Date().toISOString().split('T')[0];

const emptyCarExpense = {
  type: 'fuel',
  amount: '',
  date: today,
  description: '',
  vendor: '',
  mileage: '',
  receiptNumber: ''
};

const formatDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleDateString();
};

const cleanPayload = (data) => {
  const payload = {};
  Object.entries(data).forEach(([key, value]) => {
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) payload[key] = trimmed;
    } else if (value !== undefined && value !== null && value !== '') {
      payload[key] = value;
    }
  });
  return payload;
};

const csvEscape = (value) => {
  const text = String(value ?? '');
  if (/[",\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
};

const ModalShell = ({ title, children, onClose }) => (
  <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
    <div className="relative top-16 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">
          &times;
        </button>
      </div>
      {children}
    </div>
  </div>
);

const StatCard = ({ label, value, tone = 'text-gray-900' }) => (
  <div className="card p-4">
    <p className="text-sm font-medium text-gray-500">{label}</p>
    <p className={`mt-1 text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

const expenseTypeLabel = (type) => type?.replace('_', ' ') || 'other';

const CarLedger = () => {
  const { id } = useParams();
  const { canAccess, hasRole } = useAuth();
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const canManageCars = hasRole(['admin', 'accounts']);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm({ defaultValues: emptyCarExpense });

  const fetchCar = useCallback(async () => {
    if (!canAccess('cars')) return;

    try {
      setLoading(true);
      const response = await api.get(`/api/cars/${id}`);
      setCar(response.data.car);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch vehicle statement');
    } finally {
      setLoading(false);
    }
  }, [canAccess, id]);

  useEffect(() => {
    fetchCar();
  }, [fetchCar]);

  const expenses = useMemo(() => {
    return [...(car?.expenses || [])].sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [car]);

  const summary = useMemo(() => {
    return expenses.reduce(
      (acc, expense) => {
        const type = expense.type || 'other';
        acc.total += Number(expense.amount || 0);
        acc.count += 1;
        if (!acc.byType[type]) acc.byType[type] = { count: 0, amount: 0 };
        acc.byType[type].count += 1;
        acc.byType[type].amount += Number(expense.amount || 0);
        return acc;
      },
      { total: 0, count: 0, byType: {} }
    );
  }, [expenses]);

  const latestMileage = useMemo(() => {
    return expenses.find((expense) => expense.mileage)?.mileage || 'N/A';
  }, [expenses]);

  const openExpenseModal = () => {
    reset(emptyCarExpense);
    setShowExpenseModal(true);
  };

  const closeExpenseModal = () => {
    setShowExpenseModal(false);
  };

  const onExpenseSubmit = async (data) => {
    try {
      await api.post(`/api/cars/${id}/expense`, cleanPayload(data));
      toast.success('Vehicle expense added');
      closeExpenseModal();
      reset(emptyCarExpense);
      fetchCar();
    } catch (error) {
      const firstValidation = error.response?.data?.errors?.[0]?.msg;
      toast.error(firstValidation || error.response?.data?.message || 'Failed to add vehicle expense');
    }
  };

  const exportCsv = () => {
    if (!car) return;

    const rows = [
      ['Vehicle', 'License Plate', 'Date', 'Type', 'Description', 'Vendor', 'Receipt Number', 'Mileage', 'Amount', 'Added By'],
      ...expenses.map((expense) => [
        car.name,
        car.licensePlate,
        formatDate(expense.date),
        expenseTypeLabel(expense.type),
        expense.description,
        expense.vendor,
        expense.receiptNumber,
        expense.mileage,
        Number(expense.amount || 0),
        expense.addedBy?.name || ''
      ])
    ];

    const csv = rows.map((row) => row.map(csvEscape).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `vehicle-${car.licensePlate || car.name}-statement.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${expenses.length} vehicle statement rows`);
  };

  const printStatement = () => {
    window.print();
  };

  if (!canAccess('cars')) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">You don't have permission to access this page.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!car) {
    return (
      <div className="space-y-4">
        <Link to="/cars" className="text-primary-600 hover:text-primary-900 inline-flex items-center">
          <ArrowLeftIcon className="h-4 w-4 mr-2" />
          Back to Cars
        </Link>
        <div className="card p-8 text-center text-gray-500">Vehicle not found</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <Link to="/cars" className="text-primary-600 hover:text-primary-900 inline-flex items-center text-sm mb-2">
            <ArrowLeftIcon className="h-4 w-4 mr-2" />
            Back to Cars
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">{car.name}</h1>
          <p className="text-gray-600">
            {car.licensePlate} · {[car.make, car.model, car.year].filter(Boolean).join(' ') || 'Vehicle statement'}
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row no-print">
          <button type="button" onClick={exportCsv} className="btn-secondary">Export CSV</button>
          <button type="button" onClick={printStatement} className="btn-secondary">Print</button>
          {canManageCars && (
            <button onClick={openExpenseModal} className="btn-primary flex items-center justify-center">
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Vehicle Expense
            </button>
          )}
        </div>
      </div>

      <div className="print-only">
        <p className="text-sm text-gray-600">
          Generated {new Date().toLocaleString()} | {car.licensePlate} | {[car.make, car.model, car.year].filter(Boolean).join(' ')}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard label="Total Spend" value={formatCurrency(summary.total)} tone="text-red-600" />
        <StatCard label="Expense Entries" value={summary.count} />
        <StatCard label="Latest Mileage" value={latestMileage} />
        <StatCard label="Current Value" value={formatCurrency(car.currentValue)} tone="text-blue-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="card p-5 lg:col-span-2">
          <h2 className="font-semibold text-gray-900 mb-4">Vehicle Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div><span className="font-medium text-gray-500">Status:</span> {car.status?.replace('_', ' ')}</div>
            <div><span className="font-medium text-gray-500">Color:</span> {car.color || 'N/A'}</div>
            <div><span className="font-medium text-gray-500">VIN:</span> {car.vin || 'N/A'}</div>
            <div><span className="font-medium text-gray-500">Engine Number:</span> {car.engineNumber || 'N/A'}</div>
            <div><span className="font-medium text-gray-500">Registration Number:</span> {car.registrationNumber || 'N/A'}</div>
            <div><span className="font-medium text-gray-500">Purchase Date:</span> {formatDate(car.purchaseDate)}</div>
            <div><span className="font-medium text-gray-500">Purchase Amount:</span> {formatCurrency(car.purchaseAmount)}</div>
            <div><span className="font-medium text-gray-500">Created By:</span> {car.createdBy?.name || 'N/A'}</div>
          </div>
          {car.notes && <p className="mt-4 text-sm text-gray-600">{car.notes}</p>}
        </div>

        <div className="card p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Documents</h2>
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-medium text-gray-500">Insurance</p>
              <p>{car.insurance?.provider || 'N/A'} {car.insurance?.policyNumber ? `· ${car.insurance.policyNumber}` : ''}</p>
              <p className="text-gray-500">Expires: {formatDate(car.insurance?.expirationDate)}</p>
              <p className="text-gray-500">Premium: {formatCurrency(car.insurance?.premium)}</p>
            </div>
            <div>
              <p className="font-medium text-gray-500">Registration</p>
              <p className="text-gray-500">Expires: {formatDate(car.registration?.expirationDate)}</p>
              <p className="text-gray-500">Renewal Fee: {formatCurrency(car.registration?.renewalFee)}</p>
            </div>
          </div>
        </div>
      </div>

      {Object.keys(summary.byType).length > 0 && (
        <div className="card p-4">
          <h2 className="font-semibold text-gray-900 mb-3">Spend By Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(summary.byType).map(([type, item]) => (
              <div key={type} className="bg-gray-50 rounded-md p-3">
                <p className="capitalize text-sm font-medium text-gray-700">{expenseTypeLabel(type)}</p>
                <p className="font-semibold text-gray-900">{formatCurrency(item.amount)}</p>
                <p className="text-xs text-gray-500">{item.count} entries</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center">
          <BanknotesIcon className="h-5 w-5 text-gray-500 mr-2" />
          <h2 className="font-semibold text-gray-900">Vehicle Statement</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendor / Receipt</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Mileage</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center text-gray-500">No vehicle transactions yet</td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense._id}>
                    <td className="px-6 py-4 text-sm text-gray-600">{formatDate(expense.date)}</td>
                    <td className="px-6 py-4 text-sm capitalize text-gray-900">{expenseTypeLabel(expense.type)}</td>
                    <td className="px-6 py-4">
                      <p className="text-sm text-gray-900">{expense.description}</p>
                      <p className="text-xs text-gray-500">Added by {expense.addedBy?.name || 'N/A'}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div>{expense.vendor || 'N/A'}</div>
                      <div className="text-xs text-gray-500">{expense.receiptNumber || 'No receipt'}</div>
                    </td>
                    <td className="px-6 py-4 text-right text-sm text-gray-600">{expense.mileage || 'N/A'}</td>
                    <td className="px-6 py-4 text-right font-semibold text-gray-900">{formatCurrency(expense.amount)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showExpenseModal && (
        <ModalShell title={`Add Expense - ${car.name}`} onClose={closeExpenseModal}>
          <form onSubmit={handleSubmit(onExpenseSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Type</label>
                <select {...register('type', { required: 'Type is required' })} className="input-field">
                  <option value="fuel">Fuel</option>
                  <option value="maintenance">Maintenance</option>
                  <option value="repair">Repair</option>
                  <option value="insurance">Insurance</option>
                  <option value="registration">Registration</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="label">Amount</label>
                <input
                  {...register('amount', { required: 'Amount is required', min: { value: 0.01, message: 'Amount must be positive' } })}
                  type="number"
                  step="0.01"
                  className="input-field"
                />
                {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount.message}</p>}
              </div>
              <div>
                <label className="label">Date</label>
                <input {...register('date', { required: 'Date is required' })} type="date" className="input-field" />
              </div>
              <div>
                <label className="label">Mileage</label>
                <input {...register('mileage')} type="number" className="input-field" />
              </div>
              <div>
                <label className="label">Vendor</label>
                <input {...register('vendor')} className="input-field" />
              </div>
              <div>
                <label className="label">Receipt Number</label>
                <input {...register('receiptNumber')} className="input-field" />
              </div>
            </div>
            <div>
              <label className="label">Description</label>
              <textarea {...register('description', { required: 'Description is required' })} rows={3} className="input-field" />
              {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>}
            </div>
            <div className="flex justify-end space-x-3">
              <button type="button" onClick={closeExpenseModal} className="btn-secondary">Cancel</button>
              <button type="submit" disabled={isSubmitting} className="btn-primary">
                {isSubmitting ? 'Saving...' : 'Add Expense'}
              </button>
            </div>
          </form>
        </ModalShell>
      )}
    </div>
  );
};

export default CarLedger;
